import { useEffect, useState } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";
import type { RouteObject } from "react-router-dom";

interface IRouteErrorBoundaryProps {
  /**
   * Fills the viewport. Used where the error replaces the whole app shell -
   * the root, login and logout routes - rather than one page inside it.
   */
  fullPage?: boolean;
}

interface IErrorCopy {
  title: string;
  description: string;
}

/** Title and description for what went wrong, in words a VMS user can act on. */
const describeError = (error: unknown): IErrorCopy => {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return { title: "Page not found", description: "This page does not exist or has been moved." };
    }
    if (error.status === 401 || error.status === 403) {
      return { title: "Access denied", description: "You do not have permission to open this page." };
    }
    return {
      title: "Something went wrong",
      description: `The page could not be loaded (${error.status}${error.statusText ? ` ${error.statusText}` : ""}).`,
    };
  }
  return {
    title: "Something went wrong",
    description: "This page ran into a problem. The rest of VMS is still working - you can go back or try again.",
  };
};

/** The raw error, for the developer details panel only. */
const readErrorDetails = (error: unknown): string => {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}\n${typeof error.data === "string" ? error.data : JSON.stringify(error.data, null, 2)}`;
  }
  if (error instanceof Error) return error.stack || error.message;
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
};

/**
 * Route-level error screen, rendered by React Router in place of a route whose
 * loader or element threw. React Router clears it on the next navigation, so
 * moving to another page recovers without a reload.
 *
 * Technical details are shown in development only; users get the message and a
 * way out, never a stack trace.
 */
const RouteErrorBoundary: React.FC<IRouteErrorBoundaryProps> = ({ fullPage = false }) => {
  const error = useRouteError();
  const navigate = useNavigate();
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const { title, description } = describeError(error);
  const isDevelopment = import.meta.env.DEV;

  useEffect(() => {
    console.error("[RouteErrorBoundary]", error);
  }, [error]);

  return (
    <div
      role="alert"
      className={`flex w-full items-center justify-center bg-slate-50 px-4 py-10 ${fullPage ? "min-h-screen" : "min-h-[60vh]"}`}
    >
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
          <svg
            className="h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>
        </span>
        <h1 className="mt-4 text-base font-semibold text-gray-900">{title}</h1>
        <p className="mt-1.5 text-sm leading-6 text-slate-500">{description}</p>

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
          >
            Go back
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-[#193A6A] px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#132c52]"
          >
            Reload page
          </button>
        </div>

        {isDevelopment && (
          <div className="mt-6 text-left">
            <button
              type="button"
              onClick={() => setIsDetailsOpen((current) => !current)}
              className="text-[11px] font-medium text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline"
            >
              {isDetailsOpen ? "Hide error details" : "Show error details"}
            </button>
            {isDetailsOpen && (
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-900 p-3 text-[11px] leading-5 text-slate-100">
                {readErrorDetails(error)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Gives every route in the list its own error boundary, keeping one it already
 * declares. Applied to the pages inside the app shell, a crash then replaces
 * only that page: the sidebar and header stay usable, and the next navigation
 * clears the error.
 */
export const withRouteErrorBoundary = (routes: RouteObject[]): RouteObject[] =>
  routes.map((route) => ({ errorElement: <RouteErrorBoundary />, ...route }) as RouteObject);

export default RouteErrorBoundary;
